<script lang="ts">
    import { createEventDispatcher, onMount, tick } from 'svelte'
    import { printerParams } from './stores'
    import { SharePageDto } from './share-page.dto'
    import type { SharePageEntity } from '../../modules/toolsApi/share-pages/share-page.entity'
    import Zflow from './z/zflow.svelte'
    import Zgrid from './z/zgrid.svelte'
    import Zgrid2 from './z/zgrid2.svelte'
    import { SharePagesService } from '../../modules/toolsApi/share-pages/share-pages.service'
    import { SharePagesRepository } from '../../modules/toolsApi/share-pages/share-pages.repository'
    import { CompaniesService } from '../../modules/usersApi/companies/companies.service'
    import { CompaniesRepository } from '../../modules/usersApi/companies/companies.repository'
    import QuotationPrint from '../pos/quotation/quotation-print.svelte'
    import dayjs from 'dayjs'
    import SharePageHeader from './share-page-header.svelte'
    import SharePageFooter from './share-page-footer.svelte'
    import { ConversionsService } from '../../modules/utils/conversions.service'

    const dispatch = createEventDispatcher()

    let presentation: boolean = false

    let createdAt = new Date()

    let company = window.store?.company
    onMount(async () => {
        await evaluateSavedContent()
    })

    function expandToken(t: string) {
        t = t?.replace(':', '_')
        let sharePageId = t.split('_')[0]
        let token = t.split('_')[1]
        if (!t.includes('-')) {
            const conversions = new ConversionsService()
            sharePageId = conversions.base58ToUuid(sharePageId)
            token = conversions.base58ToUuid(token)
        }
        return { sharePageId, token }
    }

    let error = false
    let sharedPage: SharePageEntity = null
    async function evaluateSavedContent() {
        if (!window.location.pathname.startsWith('/public/share')) return
        const urlParams = new URLSearchParams(window.location.search)
        let id = urlParams.get('id')
        if (!id) return null
        const data: { sharePageId: string; token: string } = expandToken(id)
        if (!data) return
        const spS = new SharePagesService(new SharePagesRepository())
        sharedPage = await spS.publicGetOneByToken(data.sharePageId, data.token)
        if (!sharedPage) {
            error = true
            window.alert('Este documento não está mais disponível.\nO link pode estar errado ou o tempo de compartilhamento expirou.')
            return
        }
        if (!company) {
            const cS = new CompaniesService(new CompaniesRepository())
            company = await cS.getPublicCompany(sharedPage.companyId)
        }
        console.log('sharedPage', sharedPage)

        _printerParams = new SharePageDto()
        for (const item of sharedPage.data) {
            if (item.type === 'zgrid') {
                _printerParams.component.push(Zgrid)
                _printerParams.props.push(item.data)
            }
            if (item.type === 'zgrid2') {
                _printerParams.component.push(Zgrid2)
                _printerParams.props.push(item.data)
            }
            if (item.type === 'quotation-print') {
                _printerParams.component.push(QuotationPrint)
                _printerParams.props.push(item.data)
            }
        }
        createdAt = new Date(sharedPage.createdAt)
        dispatch('printing', true)
        await tick()
        _printerParams = _printerParams
        presentation = true
    }

    // Quem passa hideHeader+hideFooter traz a própria moldura (caso do Forms V2, que tem timbre
    // próprio — ver form-v2-print-frame.svelte). Nesse caso a tabela daqui não teria thead/tfoot
    // pra repetir por página: seria só um invólucro, e aninhar tabelas atrapalha a repetição do
    // cabeçalho de quem está dentro. Por isso renderizamos o conteúdo direto.
    $: useOwnFrame = !!_printerParams?.hideHeader && !!_printerParams?.hideFooter

    let _printerParams: SharePageDto = null
    printerParams.subscribe((value) => {
        company = window.store?.company
        _printerParams = value
        if (value) {
            console.log('pp', _printerParams)
            if (!_printerParams.waitLoadedEvent) goPrint()
        }
    })

    let printed = false
    function onComponentLoaded() {
        if (!printed) {
            printed = true
            goPrint()
        }
    }

    // Reset é disparado por `afterprint` (fecha assim que o usuário fecha a caixa de impressão,
    // seja imprimindo ou cancelando). O setTimeout é só um fallback de segurança caso o navegador
    // não emita o evento — resetAfterPrint() é idempotente, então disparar duas vezes é inofensivo.
    let resetFallbackTimer: ReturnType<typeof setTimeout> = null
    function resetAfterPrint() {
        clearTimeout(resetFallbackTimer)
        window.printer(null)
        printed = false
        dispatch('printing', false)
    }
    window.onafterprint = resetAfterPrint

    async function goPrint() {
        dispatch('printing', !!_printerParams)
        await tick()
        setTimeout(() => {
            window.print()
        }, 1500)
        clearTimeout(resetFallbackTimer)
        resetFallbackTimer = setTimeout(resetAfterPrint, 15000)
    }
</script>

{#if _printerParams && company}
    {#if !presentation}
        <div class="print_wait"></div>
    {/if}

    <div class="table_print_outer" class:table_print_outer_presentation={presentation}>
        {#if useOwnFrame}
            <Zflow>
                {#each _printerParams?.component as pp, index}
                    <svelte:component this={pp} on:loaded={onComponentLoaded} {..._printerParams?.props[index]}></svelte:component>
                {/each}
            </Zflow>
        {:else}
            <table class="table_print">
                {#if !_printerParams?.hideHeader}
                    <thead><tr><td> <div class="header-space">&nbsp;</div> </td></tr></thead>
                {/if}
                <tbody
                    ><tr
                        ><td>
                            <Zflow>
                                {#each _printerParams?.component as pp, index}
                                    <svelte:component this={pp} on:loaded={onComponentLoaded} {..._printerParams?.props[index]}></svelte:component>
                                {/each}
                            </Zflow>
                        </td></tr
                    ></tbody
                >
                {#if !_printerParams?.hideFooter}
                    <tfoot><tr><td> <div class="footer-space">&nbsp;</div> </td></tr></tfoot>
                {/if}
            </table>
        {/if}

        {#if !_printerParams?.hideWaterMark}
            <div class="table_print_icon">
                <img src={company.icon} />
            </div>
        {/if}

        {#if !_printerParams?.hideHeader}
            <SharePageHeader {company}></SharePageHeader>
        {/if}
        {#if !_printerParams?.hideFooter}
            <SharePageFooter {company} {createdAt}></SharePageFooter>
        {/if}
    </div>
{/if}

<style>
    .table_print {
        width: 100%;
        min-height: 100% !important;
        background-color: unset !important;
        word-break: break-word;
    }
    .table_print td {
        vertical-align: top;
    }
    .table_print tr {
    }
    .table_print .row {
        margin-left: 0px !important;
        margin-right: 0px !important;
    }
    .table_print *,
    .table_print :global(.zgrid *),
    .table_print :global(.zframe *) {
        background-color: unset !important;
    }

    .table_print :global(.zgrid .zgrid_td_mobile_title) {
        text-shadow: unset;
        color: unset;
    }
    .header-space {
        height: 60px !important;
    }
    .footer-space {
        height: 110px !important;
    }

    .print_wait {
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        background-color: #6c757d !important;
        border-left-width: 100%;
        height: 100% !important;
        z-index: 10;
    }
    .table_print * {
        max-width: 100% !important;
    }
    .table_print_outer_presentation .table_print {
        max-width: 1100px !important;
        margin: 0 auto;
        margin-top: 16px;
        background-color: white !important;
    }
    @media print {
        .print_wait {
            display: none;
        }
        .table_print_outer_presentation .table_print {
            margin-top: unset;
        }
    }
    .table_print_icon {
        opacity: 0.1;
        position: fixed;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        top: calc(50vh - 280px);
    }
    .table_print_icon img {
        height: 560px !important;
        max-height: 90%;
    }
</style>
